import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'selectedTemplateName',
    'templatePreview',
    'parametersForm',
    'phoneNumber',
    'firstName',
  ]
  static values = {
    conversationId: Number,
    mode: String,
    conversationType: String,
  }

  connect() {
    this.selectedTemplateData = null
    this.selectedTemplateId = null

    // Listen for template selection event
    this.element.addEventListener('template-selected', this.handleTemplateSelected.bind(this))
  }

  handleTemplateSelected(event) {
    this.selectedTemplateData = event.detail.templateData
    this.selectedTemplateId = event.detail.templateId

    // Setup the modal with the template data
    this.setupParametersModal()
  }

  closeModal() {
    // Find the modal element that contains this controller
    const modal = this.element.closest('dialog')
    if (modal) {
      modal.close()
    }
  }

  setupParametersModal() {
    const templateData = this.selectedTemplateData

    // Set template name
    if (this.hasSelectedTemplateNameTarget) {
      this.selectedTemplateNameTarget.textContent = templateData.name || 'Template'
    }

    // Find body component
    const bodyComponent = templateData.components?.find((comp) => comp.type === 'BODY')
    if (!bodyComponent) {
      console.error('No BODY component found in template')
      return
    }

    const templateText = bodyComponent.text || ''

    // Extract parameters — positional ({{1}}) or named ({{nome}}). Named
    // tokens keep first-occurrence order, which is the send order.
    const parameterPattern = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g
    const parameters = []
    let match

    while ((match = parameterPattern.exec(templateText)) !== null) {
      const token = match[1]
      if (!parameters.includes(token)) {
        parameters.push(token)
      }
    }

    if (parameters.every((token) => /^\d+$/.test(token))) {
      parameters.sort((a, b) => parseInt(a) - parseInt(b))
    }

    // Generate form fields for parameters
    this.generateParameterFields(parameters)

    // Set initial preview
    this.updateTemplatePreview()
  }

  generateParameterFields(parameters) {
    if (!this.hasParametersFormTarget) {
      console.error('Parameters form target not found')
      return
    }

    const form = this.parametersFormTarget
    form.innerHTML = ''

    if (parameters.length === 0) {
      form.innerHTML = '<p class="text-gray-500">Este template não possui parâmetros.</p>'
      return
    }

    parameters.forEach((token) => {
      const label = /^\d+$/.test(token)
        ? `Parâmetro {{${token}}}`
        : token.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase())
      const fieldDiv = document.createElement('div')
      fieldDiv.className = 'mb-4'
      fieldDiv.innerHTML = `
        <label class="block text-sm font-medium text-gray-700 mb-1">
          ${label}:
        </label>
        <input
          type="text"
          class="input input-bordered w-full parameter-input"
          data-param-number="${token}"
          placeholder="Digite o valor para {{${token}}}"
        />
      `
      form.appendChild(fieldDiv)
    })

    // Add event listeners to update preview
    form.querySelectorAll('.parameter-input').forEach((input) => {
      input.addEventListener('input', () => this.updateTemplatePreview())
    })
  }

  updateTemplatePreview() {
    if (!this.hasTemplatePreviewTarget) {
      console.error('Template preview target not found')
      return
    }

    const bodyComponent = this.selectedTemplateData?.components?.find(
      (comp) => comp.type === 'BODY'
    )
    if (!bodyComponent) {
      console.error('No BODY component found')
      return
    }

    let previewText = bodyComponent.text || ''

    // Replace parameters with user input
    const inputs = document.querySelectorAll('.parameter-input')

    inputs.forEach((input) => {
      const paramNumber = input.dataset.paramNumber
      const value = input.value || `{{${paramNumber}}}`
      previewText = previewText.replace(new RegExp(`\\{\\{${paramNumber}\\}\\}`, 'g'), value)
    })

    this.templatePreviewTarget.textContent = previewText
  }

  formatPhoneNumber() {
    if (!this.hasPhoneNumberTarget) return

    let value = this.phoneNumberTarget.value.replace(/\D/g, '') // Remove all non-digits
    let formattedValue = ''

    if (value.length > 0) {
      // Detect country based on first digits
      if (value.startsWith('55')) {
        // Brazilian number formatting: +55 (DD) NNNNN-NNNN
        if (value.length <= 2) {
          formattedValue = `+${value}`
        } else if (value.length <= 4) {
          formattedValue = `+${value.slice(0, 2)} (${value.slice(2)}`
        } else if (value.length <= 9) {
          formattedValue = `+${value.slice(0, 2)} (${value.slice(2, 4)}) ${value.slice(4)}`
        } else {
          formattedValue = `+${value.slice(0, 2)} (${value.slice(2, 4)}) ${value.slice(4, 9)}-${value.slice(9, 13)}`
        }
      } else if (value.startsWith('1')) {
        // US/Canada formatting: +1 (NNN) NNN-NNNN
        if (value.length <= 1) {
          formattedValue = `+${value}`
        } else if (value.length <= 4) {
          formattedValue = `+${value.slice(0, 1)} (${value.slice(1)}`
        } else if (value.length <= 7) {
          formattedValue = `+${value.slice(0, 1)} (${value.slice(1, 4)}) ${value.slice(4)}`
        } else {
          formattedValue = `+${value.slice(0, 1)} (${value.slice(1, 4)}) ${value.slice(4, 7)}-${value.slice(7, 11)}`
        }
      } else {
        // Generic international format: +CC NNNN NNNN NNNN
        if (value.length <= 2) {
          formattedValue = `+${value}`
        } else if (value.length <= 6) {
          formattedValue = `+${value.slice(0, 2)} ${value.slice(2)}`
        } else if (value.length <= 10) {
          formattedValue = `+${value.slice(0, 2)} ${value.slice(2, 6)} ${value.slice(6)}`
        } else {
          formattedValue = `+${value.slice(0, 2)} ${value.slice(2, 6)} ${value.slice(6, 10)} ${value.slice(10, 14)}`
        }
      }
    }

    this.phoneNumberTarget.value = formattedValue
  }

  sendTemplate() {
    let firstName = ''
    let phoneNumber = ''

    // For new conversations, validate contact fields
    if (this.modeValue === 'new') {
      // Validate first name
      if (!this.hasFirstNameTarget || !this.firstNameTarget.value.trim()) {
        this.showErrorNotification('Por favor, digite o primeiro nome do destinatário.')
        return
      }

      // Validate phone number
      if (!this.hasPhoneNumberTarget || !this.phoneNumberTarget.value.trim()) {
        this.showErrorNotification('Por favor, digite o número do destinatário.')
        return
      }

      firstName = this.firstNameTarget.value.trim()
      phoneNumber = this.phoneNumberTarget.value.replace(/\D/g, '') // Remove formatting for processing

      // Validate phone number length based on country code
      let isValidLength = false
      if (phoneNumber.startsWith('55')) {
        // Brazilian numbers: +55 + 2 digits (DDD) + 9 digits = 13 total
        isValidLength = phoneNumber.length === 13
      } else if (phoneNumber.startsWith('1')) {
        // US/Canada numbers: +1 + 10 digits = 11 total
        isValidLength = phoneNumber.length === 11
      } else {
        // Other countries: flexible validation (7-15 digits total)
        isValidLength = phoneNumber.length >= 7 && phoneNumber.length <= 15
      }

      if (!isValidLength) {
        this.showErrorNotification(
          'Por favor, digite um número de telefone válido para o país selecionado.'
        )
        return
      }
    } else {
      // For resume mode, we don't need contact validation since we use existing conversation data
      firstName = 'Usuario' // Default name, will be overridden by backend
      phoneNumber = '00000000000' // Default phone, will be overridden by backend
    }

    // Collect parameter values; the fields render in send order (numeric
    // sorted, named by first occurrence), so DOM order is the array order.
    const inputs = document.querySelectorAll('.parameter-input')
    const parameters = Array.from(inputs).map((input) => input.value.trim())

    if (parameters.some((value) => !value)) {
      this.showErrorNotification('Por favor, preencha todos os parâmetros do template.')
      return
    }

    const templateData = {
      template_id: this.selectedTemplateId,
      name: firstName,
      phone_number: phoneNumber,
      parameters: parameters,
    }

    // Show loading state
    const sendButton = document.querySelector(
      '[data-action="click->template-parameters#sendTemplate"]'
    )
    const originalText = sendButton.textContent
    sendButton.textContent = 'Enviando...'
    sendButton.disabled = true

    // Send template via API
    this.sendTemplateToAPI(templateData)
      .then((response) => {
        if (response.success) {
          // Close modal first
          this.closeModal()

          // Show success notification
          this.showSuccessNotification(`Template enviado com sucesso para ${firstName}!`)

          // Optionally redirect to the new conversation
          if (response.conversation_id) {
            // Substitui apenas o id numérico após a última barra, mantendo o prefixo (support_conversations ou sdr_conversations)
            const currentUrl =
              window.location.pathname + window.location.search + window.location.hash
            const newUrl = currentUrl.replace(
              /(\/[^\/]+_conversations\/)\d+/,
              `$1${response.conversation_id}`
            )
            window.location.href = newUrl
          }
        } else {
          this.showErrorNotification(`Erro ao enviar template: ${response.error}`)
        }
      })
      .catch((error) => {
        console.error('Error sending template:', error)

        // Close modal on error as well
        this.closeModal()

        this.showErrorNotification(`Erro ao enviar template: ${error.message}`)
      })
      .finally(() => {
        // Reset button state
        sendButton.textContent = originalText
        sendButton.disabled = false
      })
  }

  async sendTemplateToAPI(templateData) {
    try {
      // Choose endpoint based on mode and conversation type
      const conversationType = this.conversationTypeValue || this.detectConversationType()
      let endpoint

      if (this.modeValue === 'resume') {
        endpoint = `/${conversationType}_conversations/${this.conversationIdValue}/reengage`
      } else {
        endpoint =
          conversationType === 'sdr'
            ? '/conversations/sdr/create_from_template'
            : '/support_conversations'
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify(templateData),
      })

      if (!response.ok) {
        const errorData = await response.json()

        // Handle conversation already active case
        if (response.status === 409 && errorData.already_active) {
          throw new Error(errorData.error || 'Conversa já está ativa')
        }

        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('API call failed:', error)
      throw error
    }
  }

  showSuccessNotification(message) {
    this.createNotification(message, 'success')
  }

  showErrorNotification(message) {
    this.createNotification(message, 'error')
  }

  createNotification(message, type) {
    // Find existing notifications controller or create notification directly
    const existingNotificationController = document.querySelector(
      '[data-controller*="notifications"]'
    )

    if (existingNotificationController) {
      // Use existing notifications controller if available
      const notificationEvent = new CustomEvent('show-notification', {
        detail: { message, type },
      })
      existingNotificationController.dispatchEvent(notificationEvent)
    } else {
      // Fallback: create notification directly using the same pattern as notifications_controller
      this.createDirectNotification(message, type)
    }
  }

  createDirectNotification(message, type) {
    const toast = document.createElement('div')
    toast.className = `toast toast-bottom toast-start z-[9999]`

    const alertClass = `alert-${type === 'error' ? 'error' : 'success'}`
    const alertDiv = document.createElement('div')
    alertDiv.className = `alert ${alertClass} shadow-lg max-w-md`

    alertDiv.innerHTML = `
      <div class="flex-1">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            ${
              type === 'error'
                ? '<svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
                : '<svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>'
            }
          </div>
          <div class="ml-3">
            <div class="text-sm font-medium">${message}</div>
          </div>
        </div>
      </div>
      <div class="flex-none">
        <button class="btn btn-sm btn-ghost" onclick="this.parentElement.parentElement.remove()">
          ✕
        </button>
      </div>
    `

    toast.appendChild(alertDiv)
    document.body.appendChild(toast)

    // Auto-remove after 5 seconds for success, 8 seconds for error
    const duration = type === 'error' ? 8000 : 5000
    setTimeout(() => {
      if (toast.parentElement) {
        toast.remove()
      }
    }, duration)
  }

  detectConversationType() {
    // Try to detect conversation type from current URL
    const pathname = window.location.pathname
    if (pathname.includes('sdr_conversations')) {
      return 'sdr'
    } else if (pathname.includes('support_conversations')) {
      return 'support'
    }

    // Default fallback
    return 'support'
  }
}
